import {
  PersistedFormGroup,
  PersistedFormArray,
  PersistedArray,
} from './persisted-form-group';
import type {
  PersistedFormGroupOptions,
  PersistedFormArrayOptions,
  PersistedArrayOptions,
} from './persisted-form-group';
import type { SchemaFormControl } from '@apexdesigner/schema-forms';
import { ProcessInstance } from './process-instance';
export { ProcessInstance };
import { processInstanceSchema } from '@schemas/business-objects/process-instance';
import { PendingStateUpdateFormArray } from './pending-state-update-form-group';
import { ProcessDesignFormGroup } from './process-design-form-group';
import { ProcessInstanceHistoryFormArray } from './process-instance-history-form-group';
import { TenantFormGroup } from './tenant-form-group';
import { TokenFormArray, TokenFormGroup } from './token-form-group';
import type { Id } from '../base-types';
import type { ActivityMapping } from './activity-mapping';
import type { LatestProcessDesign } from './latest-process-design';
import type { MessageListener } from './message-listener';
import type { ServiceTask } from './service-task';
import type { Timer } from './timer';
import type { TokenHistory } from './token-history';
import type { TokenLink } from './token-link';
import type { UserTask } from './user-task';

export class ProcessInstanceFormGroup extends PersistedFormGroup {
  declare controls: {
    id: SchemaFormControl;
    referenceNumber: SchemaFormControl;
    name: SchemaFormControl;
    status: SchemaFormControl;
    data: SchemaFormControl;
    errorMessage: SchemaFormControl;
    errorCode: SchemaFormControl;
    claimedBy: SchemaFormControl;
    claimedAt: SchemaFormControl;
    completedAt: SchemaFormControl;
    loopIndex: SchemaFormControl;
    resultApplied: SchemaFormControl;
    candidateUserId: SchemaFormControl;
    createdAt: SchemaFormControl;
    updatedAt: SchemaFormControl;
    processDesignId: SchemaFormControl;
    processDesign: ProcessDesignFormGroup;
    parentTokenId: SchemaFormControl;
    parentToken: TokenFormGroup;
    processInstanceHistories: ProcessInstanceHistoryFormArray;
    pendingStateUpdates: PendingStateUpdateFormArray;
    tokens: TokenFormArray;
    tenantId: SchemaFormControl;
    tenant: TenantFormGroup;
  };

  declare value: Partial<ProcessInstance>;

  constructor(
    data?: Record<string, any> | null,
    options?: PersistedFormGroupOptions,
  ) {
    super(processInstanceSchema, ProcessInstance, data, options);
  }

  get object(): ProcessInstance {
    return new ProcessInstance(this.getRawValue());
  }

  protected override createControl(name: string) {
    switch (name) {
      case 'processDesign':
        return new ProcessDesignFormGroup();
      case 'parentToken':
        return new TokenFormGroup();
      case 'processInstanceHistories':
        return new ProcessInstanceHistoryFormArray();
      case 'pendingStateUpdates':
        return new PendingStateUpdateFormArray();
      case 'tokens':
        return new TokenFormArray();
      case 'tenant':
        return new TenantFormGroup();
      default:
        return undefined;
    }
  }

  static async find(filter?: any): Promise<ProcessInstance[]> {
    return ProcessInstance.find(filter);
  }

  static async findOne(filter?: any): Promise<ProcessInstance | null> {
    return ProcessInstance.findOne(filter);
  }

  static async findById(id: string, filter?: any): Promise<ProcessInstance> {
    return ProcessInstance.findById(id, filter);
  }

  static async create(
    data: Partial<ProcessInstance>,
  ): Promise<ProcessInstance> {
    return ProcessInstance.create(data as any);
  }

  static async updateById(
    id: string,
    updates: Partial<ProcessInstance>,
  ): Promise<ProcessInstance> {
    return ProcessInstance.updateById(id, updates as any);
  }

  static async deleteById(id: string): Promise<boolean> {
    return ProcessInstance.deleteById(id);
  }

  async migrate(options: any): Promise<any> {
    const instance = new ProcessInstance(this.getRawValue());
    return instance.migrate(options);
  }

  static async deliverMessage(options: {
    messageName: string;
    data?: Record<string, unknown>;
  }): Promise<{
    sentTo: Id[];
    started: Id[];
    failedStarts: { designId: Id; name?: string; error: string }[];
  }> {
    return ProcessInstance.deliverMessage(options);
  }

  async pause(): Promise<any> {
    const instance = new ProcessInstance(this.getRawValue());
    return instance.pause();
  }

  async processUpdates(options: any): Promise<any> {
    const instance = new ProcessInstance(this.getRawValue());
    return instance.processUpdates(options);
  }

  async resume(): Promise<any> {
    const instance = new ProcessInstance(this.getRawValue());
    return instance.resume();
  }

  async retry(): Promise<any> {
    const instance = new ProcessInstance(this.getRawValue());
    return instance.retry();
  }

  async sendMessage(options: any): Promise<any> {
    const instance = new ProcessInstance(this.getRawValue());
    return instance.sendMessage(options);
  }

  static async startFromName(options: {
    name: string;
    data?: Record<string, unknown>;
    wait?: number;
  }): Promise<any> {
    return ProcessInstance.startFromName(options);
  }

  static async start(options: {
    designId: Id;
    data?: Record<string, unknown>;
    wait?: number;
    parentTokenId?: Id;
    loopIndex?: number;
    startActivityId?: string;
    message?: Record<string, unknown>;
  }): Promise<any> {
    return ProcessInstance.start(options);
  }

  async updateData(options: any): Promise<any> {
    const instance = new ProcessInstance(this.getRawValue());
    return instance.updateData(options);
  }
}

export class ProcessInstanceFormArray extends PersistedFormArray {
  declare controls: ProcessInstanceFormGroup[];

  readonly entityName = 'ProcessInstance' as const;

  constructor(options?: PersistedFormArrayOptions) {
    super(processInstanceSchema, ProcessInstance, options);
  }

  protected override createItemGroup() {
    return new ProcessInstanceFormGroup();
  }

  get array(): ProcessInstance[] {
    return this.controls.map((group: ProcessInstanceFormGroup) => group.object);
  }
}

export class ProcessInstancePersistedArray extends PersistedArray<ProcessInstance> {
  readonly entityName = 'ProcessInstance' as const;

  constructor(options?: PersistedArrayOptions) {
    super(ProcessInstance, options);
  }
}
